/// <reference types="react-scripts" />
import { Alert, Box } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { ToolButton } from "ui/Scripts/source/components/framework/toolButton";
import { Base } from "ui/Scripts/source/framework/base";
import { DatabaseRecordType, FileType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "source/framework/appUtils";
import { IFrontPageRecords } from "source/models/front/frontPageRecords";
import { IModifiedRecordListItem } from "source/models/front/modifiedRecordListItem";
import * as documentService from "source/models/services/documentService";
import * as frontService from "source/models/services/frontService";
import * as tokenService from "source/models/services/tokenService";
import { AttachmentIcon } from "source/components/attachments/AttachmentIcon";
import { DownloadToolButton } from "source/components/framework/Button/DownloadToolButton";
import { CardContainer } from "source/components/framework/CardContainer";
import { DashboardCardProgress } from "source/components/framework/DashboardCard/DashboardCardProgress";
import { DashboardCardTitle } from "source/components/framework/DashboardCard/DashboardCardTitle";
import { SectionIcon, SectionType } from "source/components/framework/Icon/SectionIcon";
import { List } from "source/components/framework/List/List";
import { EllipsisTypography } from "source/components/framework/Typography/EllipsisTypography";
import { ListToggleButtons } from "source/components/framework/List/ListToggleButtons";

interface IModifiedRecordListProp {
    disableGlobalLoading?: boolean;
    hasCardMode?: boolean;
    userModified: boolean;
    records: IFrontPageRecords;
    getTitleSuffix: (dateRange: number) => string;
    onPropertiesClick?: () => void;
}

interface IModifiedRecordListState {
    // List
    intialized: boolean;
    isLoading: boolean;
    listModeIsLine: boolean;
    items: IModifiedRecordListItem[];
    selectedId: string;
    checkedIds: string[];

    // Edit
    editItem: any; //IToolEdit;
    showEditDialog: boolean;
}

export const ModifiedRecordList = (props: IModifiedRecordListProp) => {
    const [state, setState] = useState<IModifiedRecordListState>({
        intialized: false,
        isLoading: false,
        listModeIsLine: true,
        items: [],
        selectedId: null,
        checkedIds: [],
        editItem: null,
        showEditDialog: false
    });
    const isMountedRef = useRef<boolean>(false);
    const previousFiltersRef = useRef({
        recordCount: props.records ? (props.userModified ? props.records.userRecordCount : props.records.companyRecordCount) : 10,
        dateRange: props.records ? (props.userModified ? props.records.userDateRange : props.records.companyDateRange) : 0
    });

    const searchItems = useCallback(async () => {
        setState((old) => ({ ...old, isLoading: true }));
        const result = await AppUtils.callService(() => frontService.getModifiedRecordListItems(props.userModified), false, props.disableGlobalLoading, () => {
            if (!isMountedRef.current) return;
            setState((old) => ({ ...old, isLoading: false }));
        });
        if (!result || !isMountedRef.current) return;

        setState((old) => {
            const items = List.getListItems(old.items, old.selectedId, old.checkedIds, result.items, true, false);
            return {
                ...old,
                intialized: true,
                isLoading: false,
                items: items.items,
                selectedId: items.selectedId,
                checkedIds: items.checkedIds,
            };
        });
    }, [props.disableGlobalLoading, props.userModified]);

    useEffect(() => {
        isMountedRef.current = true;
        searchItems();
        return () => {
            isMountedRef.current = false;
        };
    }, [searchItems]);

    useEffect(() => {
        const currentFilters = {
            recordCount: props.records ? (props.userModified ? props.records.userRecordCount : props.records.companyRecordCount) : 10,
            dateRange: props.records ? (props.userModified ? props.records.userDateRange : props.records.companyDateRange) : 0
        };
        const previousFilters = previousFiltersRef.current;
        if (previousFilters.recordCount === currentFilters.recordCount && previousFilters.dateRange === currentFilters.dateRange) return;
        searchItems();
        previousFiltersRef.current = currentFilters;
    }, [props.records, props.userModified, searchItems]);

    const handleSearchLines = (_sortColumn: string, _sortOrderIsAsc: boolean, _nextPage: boolean) => {
        searchItems();
    };

    const handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        setState((old) => ({
            ...old,
            selectedId,
            checkedIds
        }));
    };
    
    const handleListModeToggle = (_event: React.MouseEvent<HTMLElement>, listModeIsLine: boolean) => {
        setState((old) => ({
            ...old,
            listModeIsLine,
        }));
    };

    const handleOpenLine = (id: string) => {
        const item = state.items.find(i => i.id === id);
        if (!item) return;
        const url = item.getRecordTypeIntraUrl();
        if (!url) return;
        tokenService.navigateTo(url);
    };

    const handleDownloadFile = async (companyId: string, id: string) => {
        if (!companyId || !id) return;
        const fileName = AppUtils.callService(() => documentService.downloadFile(companyId, id));
        if (!fileName) return;
        AppUtils.showSuccessMessage(String.format(Translations.FileParameterDownloaded, fileName));
    };

    const getRecordTypeIcon = (recordType: number, fileType?: FileType): React.ReactNode => {
        switch (recordType) {
        case DatabaseRecordType.Message:
            return <SectionIcon type={SectionType.CurrentTopics} />;
        case DatabaseRecordType.Document:
            return <SectionIcon type={SectionType.Documents} />;
        case DatabaseRecordType.File:
            return <AttachmentIcon fileType={fileType} />;
        case DatabaseRecordType.Event:
            return <SectionIcon type={SectionType.Events} />;
        case DatabaseRecordType.Task:
            return <SectionIcon type={SectionType.Tasks} />;
        case DatabaseRecordType.RecordUser:
            return <SectionIcon type={SectionType.UserRecords} />;
        case DatabaseRecordType.CompanyGroup:
            return <SectionIcon type={SectionType.CompanyGroups} />;
        case DatabaseRecordType.Company:
            return <SectionIcon type={SectionType.Companies} />;
        case DatabaseRecordType.UserGroup:
            return <SectionIcon type={SectionType.UserGroups} />;
        case DatabaseRecordType.User:
            return <SectionIcon type={SectionType.Users} />;
        case DatabaseRecordType.InstantMessage:
            return <SectionIcon type={SectionType.InstantMessages} />;
        default:
            return <></>;
        }
    };

    return (
        <>
            <>
                <DashboardCardTitle
                    title={(props.userModified ? Translations.MyLastModifications : Translations.OthersLastModifications) + props.getTitleSuffix(props.userModified ? props.records.userDateRange : props.records.companyDateRange)}
                />
                {props.hasCardMode &&
                    <ListToggleButtons value={state.listModeIsLine} onChange={handleListModeToggle} />
                }
            </>
            <CardContainer>
                <DashboardCardProgress isLoading={state.isLoading} />
                {!state.isLoading && state.items.length < 1 &&
                    <Alert severity="info">{Translations.NoModifications}</Alert>
                }
                {state.items.length > 0 &&
                    <List
                        intialized={state.intialized}
                        isLoading={state.isLoading}
                        hasMore={false}
                        header={null}
                        lines={state.listModeIsLine
                            ? state.items.map((item) => {
                                return {
                                    columns: [
                                        {
                                            size: 1,
                                            content: item.recordType !== DatabaseRecordType.File
                                                ? <AvatarImage
                                                    name={item.getRecordTypeTitle()}
                                                    icon={getRecordTypeIcon(item.recordType, item.fileType)}
                                                    tooltip={item.getRecordTypeTitle()}
                                                    size={30}
                                                />
                                                : <Box sx={{ margin: ".25rem" }}><AttachmentIcon fileType={item.fileType} color="#e9f0fa" strokeColor="#848688" /></Box>
                                        },
                                        {
                                            size: 3,
                                            content: Base.timeToDateTimeStr(item.modifyDate),
                                        },
                                        {
                                            size: props.userModified ? 8 : 7,
                                            content:
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "space-between",
                                                        flexGrow: 1,
                                                        maxWidth: "100%"
                                                    }}
                                                >
                                                    <EllipsisTypography>{item.title}</EllipsisTypography>
                                                    {item.isFile() &&
                                                        <DownloadToolButton onClick={() => handleDownloadFile(item.companyId, item.id)} />
                                                    }
                                                </Box>
                                        },
                                        {
                                            size: 1,
                                            content: (!props.userModified && <AvatarImage
                                                id={item.userCode}
                                                name={item.userName}
                                                image={item.userPicture}
                                                tooltip={item.userName}
                                                size={30}
                                            />)
                                        }
                                    ],
                                    id: item.id,
                                };
                            })
                            : null}
                        cards={null}
                        sortColumn={""}
                        sortOrderIsAsc={true}
                        selectedId={state.selectedId}
                        checkedIds={state.checkedIds}
                        onSetSelectedIds={handleSetSelectedIds}
                        onSearchLines={handleSearchLines}
                        onOpenLine={handleOpenLine}
                    />
                }
                {!!props.onPropertiesClick &&
                    <ToolButton
                        tooltip={Translations.Properties}
                        classes={"properties"}
                        onClick={props.onPropertiesClick}
                    />
                }
            </CardContainer>
        </>
    );
};